// src/components/admin/GestionLugares.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import DatosMaestrosService from '../../services/datosMaestrosService';
import ModalConfirmacionCambioEstado from '../ModalConfirmacionCambioEstado';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionDatosMaestros.css';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../mobile/BottomSheet';
import BotonFiltros from '../mobile/BotonFiltros';
import ChipsFiltros from '../mobile/ChipsFiltros';
import { coincideBusqueda } from '../../utils/textoComparable';

const GestionLugares = () => {
    const [lugares, setLugares] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedLugar, setSelectedLugar] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        direccion: '',
        tipo: '',
        telefono: '',
        descripcion: ''
    });
    const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
    
    // Estados para el modal de confirmación de eliminación
    const [showModalEliminar, setShowModalEliminar] = useState(false);
    const [lugarAEliminar, setLugarAEliminar] = useState(null);
    const [eliminando, setEliminando] = useState(false);
    
    // Filtros
    const [filterNombre, setFilterNombre] = useState('');
    const [filterTipo, setFilterTipo] = useState('TODOS');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    useEffect(() => {
        cargarLugares();
    }, []);

    const cargarLugares = async () => {
        setLoading(true);
        try {
            const response = await DatosMaestrosService.getLugares();
            setLugares(response.data);
        } catch (error) {
            mostrarAlerta('Error al cargar lugares', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const mostrarAlerta = (message, variant) => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
    };

    const handleOpenModal = (mode, lugar = null) => {
        setModalMode(mode);
        if (mode === 'edit' && lugar) {
            setSelectedLugar(lugar);
            setFormData({
                nombre: lugar.nombre,
                direccion: lugar.direccion || '',
                tipo: lugar.tipo || '',
                telefono: lugar.telefono || '',
                descripcion: lugar.descripcion || ''
            });
        } else {
            setSelectedLugar(null);
            setFormData({ nombre: '', direccion: '', tipo: '', telefono: '', descripcion: '' });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedLugar(null);
        setFormData({ nombre: '', direccion: '', tipo: '', telefono: '', descripcion: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (modalMode === 'create') {
                await DatosMaestrosService.createLugar(formData);
                mostrarAlerta('✅ Lugar creado exitosamente', 'success');
            } else {
                await DatosMaestrosService.updateLugar(selectedLugar.id, formData);
                mostrarAlerta('✅ Lugar actualizado exitosamente', 'success');
            }
            handleCloseModal();
            // Refresh automático de la lista
            await cargarLugares();
        } catch (error) {
            mostrarAlerta('❌ Error al guardar el lugar', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id) => {
        const lugar = lugares.find(l => l.id === id);
        setLugarAEliminar(lugar);
        setShowModalEliminar(true);
    };

    const confirmarEliminar = async () => {
        if (!lugarAEliminar) return;
        
        setEliminando(true);
        setLoading(true);
        try {
            await DatosMaestrosService.deleteLugar(lugarAEliminar.id);
            mostrarAlerta('✅ Lugar eliminado exitosamente', 'success');
            setShowModalEliminar(false);
            setLugarAEliminar(null);
            // Refresh automático de la lista
            await cargarLugares();
        } catch (error) {
            mostrarAlerta('❌ Error al eliminar el lugar. Puede estar siendo utilizado en algún trámite.', 'danger');
            setShowModalEliminar(false);
            setLugarAEliminar(null);
        } finally {
            setEliminando(false);
            setLoading(false);
        }
    };

    const cancelarEliminar = () => {
        setShowModalEliminar(false);
        setLugarAEliminar(null);
    };

    const getTipoBadgeColor = (tipo) => {
        const colors = {
            'Juzgado': 'primary',
            'Tribunal': 'dark',
            'Consultorio': 'danger',
            'Oficina': 'success',
            'Sala': 'info',
            'Dependencia': 'warning',
            'Otro': 'secondary'
        };
        return colors[tipo] || 'secondary';
    };

    // Función para filtrar lugares
    const getLugaresFiltrados = () => {
        let lugaresFiltrados = [...lugares];

        // Filtrar por nombre
        if (filterNombre.trim()) {
            lugaresFiltrados = lugaresFiltrados.filter(l => coincideBusqueda(l.nombre, filterNombre));
        }

        // Filtrar por tipo
        if (filterTipo !== 'TODOS') {
            lugaresFiltrados = lugaresFiltrados.filter(l => l.tipo === filterTipo);
        }

        return lugaresFiltrados;
    };

    return (
        <div className="gestion-datos-maestros">
            {alert.show && (
                <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex flex-wrap gap-2">
                    <Button
                        variant="outline-secondary" 
                        onClick={cargarLugares}
                        disabled={loading}
                        className="shadow-sm"
                        title="Recargar lista"
                    >
                        <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => handleOpenModal('create')}
                        className="shadow-sm"
                    >
                        <i className="bi bi-plus-circle me-2"></i>
                        Nuevo Lugar
                    </Button>
                </div>
            </div>

            {/* Filtros */}
            {(() => {
                const limpiarFiltros = () => { setFilterNombre(''); setFilterTipo('TODOS'); };
                const chipsFiltros = [
                    filterNombre.trim() !== '' && { key: 'nombre', label: filterNombre, onQuitar: () => setFilterNombre('') },
                    filterTipo !== 'TODOS' && { key: 'tipo', label: filterTipo, onQuitar: () => setFilterTipo('TODOS') },
                ].filter(Boolean);
                const resumen = `Mostrando ${getLugaresFiltrados().length} de ${lugares.length} lugares`;

                const campoBuscar = (
                    <CampoFiltro etiqueta="Buscar" icono="bi-search" htmlFor="filtro-lugar-nombre" ancho>
                        <Form.Control
                            id="filtro-lugar-nombre"
                            className="panel-filtros-input"
                            type="text"
                            placeholder="Nombre del lugar…"
                            value={filterNombre}
                            onChange={(e) => setFilterNombre(e.target.value)}
                        />
                    </CampoFiltro>
                );
                const campoTipo = (
                    <CampoFiltro etiqueta="Tipo" icono="bi-funnel" htmlFor="filtro-lugar-tipo">
                        <Form.Select
                            id="filtro-lugar-tipo"
                            className="panel-filtros-input"
                            value={filterTipo}
                            onChange={(e) => setFilterTipo(e.target.value)}
                        >
                            <option value="TODOS">Todos</option>
                            <option value="Juzgado">Juzgado</option>
                            <option value="Tribunal">Tribunal</option>
                            <option value="Consultorio">Consultorio</option>
                            <option value="Oficina">Oficina</option>
                            <option value="Sala">Sala</option>
                            <option value="Dependencia">Dependencia</option>
                            <option value="Otro">Otro</option>
                        </Form.Select>
                    </CampoFiltro>
                );

                return esMobile ? (
                    <div className="mt-3 mb-3">
                        <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                        <ChipsFiltros chips={chipsFiltros} />
                        {sheetFiltrosAbierto && (
                            <BottomSheet
                                titulo="Filtros"
                                onCerrar={() => setSheetFiltrosAbierto(false)}
                                accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                            >
                                {campoBuscar}
                                {campoTipo}
                            </BottomSheet>
                        )}
                        <small className="d-block mt-2 text-muted">{resumen}</small>
                    </div>
                ) : (
                    <PanelFiltros hayFiltros={chipsFiltros.length > 0} onLimpiar={limpiarFiltros} resumen={resumen}>
                        {campoBuscar}
                        {campoTipo}
                    </PanelFiltros>
                );
            })()}

            {loading && lugares.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Tipo</th>
                                <th>Dirección</th>
                                <th>Teléfono</th>
                                <th>Descripción</th>
                                <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {getLugaresFiltrados().length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center text-muted py-4">
                                        {lugares.length === 0 
                                            ? 'No hay lugares registrados' 
                                            : 'No se encontraron lugares con los filtros aplicados'}
                                    </td>
                                </tr>
                            ) : (
                                getLugaresFiltrados().map((lugar) => (
                                    <tr key={lugar.id}>
                                        <td className="fw-semibold">
                                            <i className="bi bi-geo-alt text-primary me-2"></i>
                                            {lugar.nombre}
                                        </td>
                                        <td>
                                            {lugar.tipo ? (
                                                <span className={`badge bg-${getTipoBadgeColor(lugar.tipo)}`}>
                                                    {lugar.tipo}
                                                </span>
                                            ) : (
                                                <span className="text-muted fst-italic">Sin tipo</span>
                                            )}
                                        </td>
                                        <td>
                                            {lugar.direccion ? (
                                                <>
                                                    <i className="bi bi-building me-1 text-muted"></i>
                                                    {lugar.direccion}
                                                </>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            {lugar.telefono ? (
                                                <>
                                                    <i className="bi bi-telephone me-1 text-muted"></i>
                                                    {lugar.telefono}
                                                </>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={lugar.descripcion}>
                                                {lugar.descripcion || <span className="text-muted fst-italic">Sin descripción</span>}
                                            </div>
                                        </td>
                                        <td className="text-end">
                                            <MenuAccionesFila acciones={[
                                                { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', lugar) },
                                                { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleDelete(lugar.id) },
                                            ]} />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModal} onHide={handleCloseModal} centered scrollable>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>
                        <i className={`bi ${modalMode === 'create' ? 'bi-plus-circle' : 'bi-pencil-square'} me-2 text-primary`}></i>
                        {modalMode === 'create' ? 'Nuevo Lugar' : 'Editar Lugar'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="p-4">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">
                                <i className="bi bi-tag-fill me-2 text-primary"></i>
                                Nombre *
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.nombre}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                required
                                placeholder="Ej: Tribunal Federal N°1, Consultorio Dr. Pérez, Oficina ANSES, etc."
                                className="shadow-sm"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">
                                <i className="bi bi-list-ul me-2 text-success"></i>
                                Tipo
                            </Form.Label>
                            <Form.Select
                                value={formData.tipo}
                                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                                className="shadow-sm"
                            >
                                <option value="">Seleccione un tipo...</option>
                                <option value="Juzgado">⚖️ Juzgado</option>
                                <option value="Tribunal">🏛️ Tribunal</option>
                                <option value="Consultorio">🏥 Consultorio</option>
                                <option value="Oficina">🏢 Oficina</option>
                                <option value="Sala">🚪 Sala</option>
                                <option value="Dependencia">🏢 Dependencia</option>
                                <option value="Otro">📍 Otro</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">
                                <i className="bi bi-building me-2 text-info"></i>
                                Dirección
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.direccion}
                                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                                placeholder="Ej: Calle 123, La Plata"
                                className="shadow-sm"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">
                                <i className="bi bi-telephone me-2 text-warning"></i>
                                Teléfono
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.telefono}
                                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                                placeholder="Ej: 0221-1234567"
                                className="shadow-sm"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">
                                <i className="bi bi-card-text me-2 text-secondary"></i>
                                Descripción
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.descripcion}
                                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                placeholder="Información adicional sobre el lugar"
                                className="shadow-sm"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="bg-light">
                        <Button variant="light" onClick={handleCloseModal} className="px-4">
                            <i className="bi bi-x-circle me-2"></i>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading} className="px-4 shadow-sm">
                            {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-circle me-2"></i>
                                    Guardar
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de confirmación para eliminar lugar */}
            <ModalConfirmacionCambioEstado
                isOpen={showModalEliminar}
                onClose={cancelarEliminar}
                onConfirm={confirmarEliminar}
                titulo="Eliminar Lugar"
                mensaje={
                    lugarAEliminar
                        ? `¿Está seguro de eliminar el lugar "${lugarAEliminar.nombre}"?\n\nEsta acción no se puede deshacer.`
                        : '¿Está seguro de que desea eliminar este lugar?'
                }
                cargando={eliminando}
                textoBotonConfirmar="Eliminar"
                textoBotonCancelar="Cancelar"
                variantBotonConfirmar="danger"
            />
        </div>
    );
};

export default GestionLugares;
